import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getDailyPrompt } from '@/data/dailyPrompts';


import WordOfTheDay from '@/components/WordOfTheDay';
import {
  Sparkles, Heart, MessageCircle, Share2, ArrowRight, Wand2, Trophy,
  ChevronRight, Users, Clock, Check, Eye, Shield,
  Flame, Sun, Smile, Leaf, Award, Dog, Coffee, Mountain, Activity, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Nav, Footer, GrainOverlay, Glow, Reveal, Overline,
} from '@/components/landing/LandingShell';
import DailyChallenge from '@/components/DailyChallenge';


import SafeImage from '@/components/SafeImage';
import AdminDeleteButton from '@/components/AdminDeleteButton';
import ScrollToTopPill from '@/components/ScrollToTopPill';
import InViewVideo from '@/components/InViewVideo';
import AppStoreBadges from '@/components/AppStoreBadges';
import NativePrelogin from '@/pages/NativePrelogin';
import ZenTile from '@/components/shared/ZenTile';
import { quizLabel } from '@/lib/quizLabel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// True ONLY when running inside the Capacitor iOS / Android shell.  Web
// browsers (mobile or desktop) always return false, so the public website
// experience is untouched.
const IS_NATIVE_APP = (() => {
  try {
    if (typeof window === 'undefined') return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
    return !!cap.isNative || ['ios', 'android'].includes(cap.getPlatform && cap.getPlatform());
  } catch { return false; }
})();

// Most-loved creations (hand-picked, Feb-2026). Users pick the exact media.
//   Stills (3, user-supplied Feb 9 2026):
//     - Unsplash 1500514966906 = Aerial view of a wooden boat on a white sand beach
//     - Unsplash 1574158622682 = A tabby cat with green eyes against a blue sky
//     - /assets/popular/happy-dog.jpg = A smiling black-and-white dog on a walk
//   Videos (2, Pexels free CDN):
//     - 1409899 = Waves Rushing And Splashing To The Shore
//     - 15776898 = A ski resort covered in snow and trees
const POPULAR_POSTS_FALLBACK = [
  { id: 'pp-boat', url: 'https://images.unsplash.com/photo-1500514966906-fe245eea9344?w=1200&q=75', prompt: 'An aerial view of a boat resting on turquoise shallows', caption: 'A morning where the tide forgot to hurry.', user: { username: 'aiya.studio' }, likes_count: 1284, comments_count: 96, shares_count: 38 },
  {
    id: 'pp-video-waves',
    type: 'video',
    url: 'https://videos.pexels.com/video-files/1409899/1409899-hd_1920_1080_25fps.mp4',
    poster_url: 'https://images.pexels.com/videos/1409899/pictures/preview-1.jpg',
    prompt: 'A beautiful sea with crashing waves',
    caption: 'The ocean, keeping its own kind of time.',
    user: { username: 'ocean.notes' },
    likes_count: 2104,
    comments_count: 128,
    shares_count: 71,
  },
  { id: 'pp-cat', url: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=1200&q=75', prompt: 'A green-eyed tabby cat against a bright blue sky', caption: 'A small witness to a very large sky.', user: { username: 'naoki.draws' }, likes_count: 942, comments_count: 71, shares_count: 24 },
  { id: 'pp-dog', url: '/assets/popular/happy-dog.jpg', prompt: 'A joyful dog mid-walk on a quiet city street', caption: 'Joy, unhurried, moving at a wagging pace.', user: { username: 'soft.maker' }, likes_count: 1766, comments_count: 118, shares_count: 42 },
  {
    id: 'pp-video-ski',
    type: 'video',
    url: 'https://videos.pexels.com/video-files/15776898/15776898-hd_1920_1080_30fps.mp4',
    poster_url: 'https://images.pexels.com/photos/10988136/pexels-photo-10988136.jpeg?auto=compress&cs=tinysrgb&w=900',
    prompt: 'A snowy mountain with trees and a ski lodge',
    caption: 'A place where the noise of the week melts off you.',
    user: { username: 'winter.calm' },
    likes_count: 1673,
    comments_count: 84,
    shares_count: 42,
  },
];

const TRENDING_CATEGORIES = [
  { label: 'AI Art', Icon: Sparkles }, { label: 'Funny Memes', Icon: Smile },
  { label: 'Family Friendly', Icon: Heart }, { label: 'Motivation', Icon: Flame },
  { label: 'Animals', Icon: Dog }, { label: 'Food', Icon: Coffee },
  { label: 'Wellness', Icon: Leaf }, { label: 'Sports', Icon: Activity },
  { label: 'Positive News', Icon: Sun }, { label: 'Nature', Icon: Mountain },
  { label: 'Awards', Icon: Award },
];

const COMMUNITY_FEED_FILTERS = ['Newest', 'Popular', 'Trending', 'Following', 'Staff Picks'];

const COMMUNITY_FEED_FALLBACK = [
  { id: 'cf1', url: 'https://images.pexels.com/photos/21031988/pexels-photo-21031988.jpeg?auto=compress&cs=tinysrgb&w=900', prompt: 'Slow morning, no agenda', user: { username: 'kw.studio' }, likes_count: 412 },
  { id: 'cf2', url: 'https://images.pexels.com/photos/8727381/pexels-photo-8727381.jpeg?auto=compress&cs=tinysrgb&w=900', prompt: 'Studies of light', user: { username: 'haru.draws' }, likes_count: 287 },
  { id: 'cf3', url: 'https://images.unsplash.com/photo-1444044205806-38f3ed106c10?w=900&q=70', prompt: 'Where the day softens', user: { username: 'soren.ai' }, likes_count: 196 },
  { id: 'cf4', url: 'https://images.pexels.com/photos/8180654/pexels-photo-8180654.jpeg?auto=compress&cs=tinysrgb&w=900', prompt: 'A garden remembering rain', user: { username: 'iris.tanaka' }, likes_count: 358 },
  { id: 'cf5', url: 'https://images.unsplash.com/photo-1666692266862-d336f746d46d?w=900&q=70', prompt: 'Twilight, somewhere out west', user: { username: 'cal.makes' }, likes_count: 244 },
  { id: 'cf6', url: 'https://images.pexels.com/photos/15791105/pexels-photo-15791105.jpeg?auto=compress&cs=tinysrgb&w=900', prompt: 'Bonsai #07', user: { username: 'mira.tanaka' }, likes_count: 521 },
];

// Local lighthouse-by-the-ocean image (user-provided) with remote onError fallbacks.
const LIGHTHOUSE_IMG = '/assets/demo/lighthouse.png';
const LIGHTHOUSE_FALLBACKS = [
  'https://images.unsplash.com/photo-1455587734955-081b22074882?w=900&q=70',
  'https://images.unsplash.com/photo-1571893544028-06b07af6dade?w=900&q=70',
];
// Hero-mockup demo rotation. Each scene runs the full type → generate →
// result phase loop, then we cycle to the next one so returning visitors
// see three different creations instead of one repeating clip.
const DEMO_SCENES = [
  {
    prompt: 'Create a lighthouse by the ocean',
    img: LIGHTHOUSE_IMG,
    fallbacks: LIGHTHOUSE_FALLBACKS,
    alt: 'lighthouse by the ocean',
  },
  {
    prompt: 'An aerial shot of a beautiful ocean with waves crashing in',
    img: 'https://images.pexels.com/videos/1409899/pictures/preview-1.jpg',
    fallbacks: [
      'https://images.unsplash.com/photo-1500514966906-fe245eea9344?w=900&q=75',
    ],
    alt: 'aerial ocean with crashing waves',
  },
  {
    prompt: 'Horses galloping across a farm at golden hour',
    type: 'video',
    img: 'https://videos.pexels.com/video-files/7460273/7460273-hd_1920_1080_25fps.mp4',
    poster: 'https://images.pexels.com/videos/7460273/pictures/preview-0.jpg',
    fallbacks: [
      'https://videos.pexels.com/video-files/8625399/8625399-hd_1920_1080_30fps.mp4',
    ],
    alt: 'horses galloping across a farm',
  },
];

// Loop phases (cumulative ms): 0-1s idle → 1-5s typing → 5-6s ready → 6-9s generating → 9-13s result → loop
const PHASES = [
  { name: 'idle',       duration: 1000 },
  { name: 'typing',     duration: 4000 },
  { name: 'ready',      duration: 1000 },
  { name: 'generating', duration: 3000 },
  { name: 'result',     duration: 4000 },
];
const LOOP_MS = PHASES.reduce((s, p) => s + p.duration, 0);

function usePhaseLoop() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf;
    let start = performance.now();
    const tick = (now) => {
      setT(((now - start) % LOOP_MS));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  let acc = 0;
  for (const p of PHASES) {
    if (t < acc + p.duration) return { phase: p.name, elapsed: t - acc, duration: p.duration };
    acc += p.duration;
  }
  return { phase: 'result', elapsed: 0, duration: 1 };
}

function PhoneMockup() {
  const { phase, elapsed, duration } = usePhaseLoop();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [imgFallbackIdx, setImgFallbackIdx] = useState(-1); // -1 means use primary
  const scene = DEMO_SCENES[sceneIdx];
  const currentImg = imgFallbackIdx === -1 ? scene.img : scene.fallbacks[imgFallbackIdx];

  // Advance to the next scene at the end of each loop (result → idle).
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (prevPhase.current === 'result' && phase === 'idle') {
      setSceneIdx((i) => (i + 1) % DEMO_SCENES.length);
      setImgFallbackIdx(-1);
    }
    prevPhase.current = phase;
  }, [phase]);

  // Preload every candidate IMAGE so the swap is instant if a fallback is needed.
  // Video scenes rely on `preload="auto"` on the <video> element itself.
  useEffect(() => {
    DEMO_SCENES.forEach((s) => {
      if (s.type === 'video') return;
      [s.img, ...s.fallbacks].forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, []);

  // How much of the prompt to show in the textarea based on current phase
  let visibleChars = 0;
  if (phase === 'typing') visibleChars = Math.floor((elapsed / duration) * scene.prompt.length);
  else if (phase === 'ready' || phase === 'generating' || phase === 'result') visibleChars = scene.prompt.length;
  const typedText = scene.prompt.slice(0, visibleChars);

  const isGenerating = phase === 'generating';
  const showResult = phase === 'result';
  const progressPct = isGenerating ? Math.min(100, Math.round((elapsed / duration) * 100)) : 0;

  return (
    <div data-testid="hero-phone-mockup" className="relative mx-auto" style={{ width: 300, height: 600 }}>
      <div aria-hidden className="absolute -inset-10 bg-[#1C4749]/20 blur-[80px] rounded-full" />
      <div className="relative rounded-[3rem] border-[8px] border-[#1C2541] bg-[#0A1514] overflow-hidden h-full w-full shadow-[0_0_80px_rgba(28,71,73,0.18)]">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-[#1C2541] z-10" />
        {/* Status bar */}
        <div className="absolute top-1 left-0 right-0 flex justify-between px-6 text-[10px] text-white/60 z-10">
          <span>9:41</span><span>●●●</span>
        </div>

        {/* Studio header */}
        <div className="absolute inset-x-0 top-10 px-4 pb-2 z-[5]">
          <p className="text-[8px] tracking-[0.22em] uppercase font-bold text-[#1C4749]/80">Studio</p>
          <p className="text-base text-white" style={{ fontFamily: 'var(--font-display)' }}>Create</p>
        </div>

        {/* Body */}
        <div className="absolute inset-x-0 top-[88px] bottom-3 px-3 overflow-hidden">
          {/* Type selector — reflects whichever medium the current scene
              is generating so the mockup reads "Video" for the horse clip
              and "Image" for still creations. */}
          <div className="flex gap-1.5 mb-3">
            <span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold border ${scene.type === 'video' ? 'bg-white/5 text-white/70 border-white/10' : 'bg-[#1C4749] text-white border-transparent'}`}>Image</span>
            <span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold border ${scene.type === 'video' ? 'bg-[#1C4749] text-white border-transparent' : 'bg-white/5 text-white/70 border-white/10'}`}>Video</span>
            <span className="px-2.5 py-1 rounded-full bg-white/5 text-white/70 text-[9px] border border-white/10">Meme</span>
          </div>

          <AnimatePresence mode="wait">
            {!showResult && (
              <motion.div
                key="prompt-stage"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
              >
                {/* Prompt card */}
                <div className="rounded-2xl bg-[#101F1D]/80 border border-white/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[8px] uppercase tracking-wider font-semibold text-[#1C4749]/90">{scene.type === 'video' ? 'Describe your video' : 'Describe your image'}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFD166]/15 text-[#FFD166] text-[8px] font-semibold border border-[#FFD166]/30">✨ Try example</span>
                  </div>
                  <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3 min-h-[60px]">
                    <p className="text-[11px] text-white leading-snug">
                      {typedText || <span className="text-white/40">A peaceful zen garden with cherry blossoms…</span>}
                      {(phase === 'idle' || phase === 'typing' || phase === 'ready') && (
                        <span className="inline-block w-px h-3 align-middle bg-[#1C4749] ml-0.5 animate-pulse" />
                      )}
                    </p>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="px-2 py-0.5 rounded-full bg-[#1C4749]/10 text-[#1C4749] text-[8px] border border-[#1C4749]/20">mountain lake</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#1C4749]/10 text-[#1C4749] text-[8px] border border-[#1C4749]/20">bamboo forest</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#1C4749]/10 text-[#1C4749] text-[8px] border border-[#1C4749]/20">zen garden</span>
                  </div>
                </div>

                {/* Refine row */}
                <div className="mt-2 rounded-2xl bg-[#101F1D]/80 border border-white/10 px-3 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-white font-semibold">Refine your post</p>
                    <p className="text-[8px] text-[#94A3B8]">Style · Mood · Lighting</p>
                  </div>
                  <span className="text-[#94A3B8] text-xs">›</span>
                </div>

                {/* Generate / Generating button */}
                <motion.div
                  animate={phase === 'ready' ? { scale: [1, 1.04, 1] } : { scale: 1 }}
                  transition={{ duration: 0.7, repeat: phase === 'ready' ? Infinity : 0 }}
                  className="mt-3"
                >
                  <div className={`relative w-full overflow-hidden inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-xl py-2.5 shadow-[0_0_18px_rgba(28,71,73,0.4)] ${isGenerating ? 'bg-[#1C4749]/40 text-white' : 'bg-[#1C4749] text-white'}`}>
                    {isGenerating ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                        Generating… {progressPct}%
                        <span
                          className="absolute left-0 bottom-0 h-0.5 bg-[#1C4749]"
                          style={{ width: `${progressPct}%`, transition: 'width 0.15s linear' }}
                        />
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3z"/></svg>
                        {scene.type === 'video' ? 'Generate Video' : 'Generate Image'}
                      </>
                    )}
                  </div>
                </motion.div>

                <p className="mt-2 text-center text-[8px] text-[#94A3B8]/80">{scene.type === 'video' ? '~9 seconds with fal.ai Ray-2 Flash' : '~6 seconds with Gemini Nano-Banana'}</p>
              </motion.div>
            )}

            {showResult && (
              <motion.div
                key="result-stage"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="rounded-2xl overflow-hidden bg-[#101F1D] border border-[#1C4749]/30 shadow-[0_0_30px_rgba(28,71,73,0.2)]">
                  {scene.type === 'video' ? (
                    <video
                      key={`${sceneIdx}-${imgFallbackIdx}`}
                      src={currentImg}
                      poster={scene.poster}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      className="w-full aspect-[4/5] object-cover"
                      onError={() => {
                        setImgFallbackIdx((idx) => (idx + 1 < scene.fallbacks.length ? idx + 1 : idx));
                      }}
                    />
                  ) : (
                    <img
                      src={currentImg}
                      alt={scene.alt}
                      className="w-full aspect-[4/5] object-cover"
                      loading="eager"
                      onError={() => {
                        // Walk down the fallback list if the current source fails to load.
                        setImgFallbackIdx((idx) => (idx + 1 < scene.fallbacks.length ? idx + 1 : idx));
                      }}
                    />
                  )}
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#1C4749]/80 font-semibold">Your creation</p>
                    <p className="text-[11px] text-white leading-snug mt-1" style={{ fontFamily: 'var(--font-display)' }}>&ldquo;{scene.prompt}&rdquo;</p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <div className="text-[10px] font-semibold rounded-xl py-2 text-center bg-[#1C4749] text-white shadow-[0_0_14px_rgba(28,71,73,0.35)]">Share to feed</div>
                  <div className="text-[10px] font-semibold rounded-xl py-2 text-center bg-white/5 text-white border border-white/10">Regenerate</div>
                </div>
                <p className="mt-2 text-center text-[8px] text-[#1C4749]/80">{scene.type === 'video' ? 'Generated in 8.4s · fal.ai Ray-2 Flash' : 'Generated in 5.8s · Gemini Nano-Banana'}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Hero({ navigate, posts, token }) {
  // The public landing renders the nav inline (`staticHeader`) so the header
  // scrolls away with the page on iOS.  In that mode the hero no longer needs
  // the 36/44-unit top padding that compensated for a fixed header.
  const isLanding = !token;
  return (
    <section
      data-testid="hero-section"
      className={`relative ${isLanding ? 'pt-8 md:pt-12' : 'zs-below-header pt-36 md:pt-44'} pb-6 md:pb-8 overflow-hidden`}
      style={!isLanding ? { '--zs-header-gap': '144px' } : undefined}
    >
      <Glow className="-top-32 -left-32 w-[520px] h-[520px]" color="#1C4749" opacity={0.18} />
      <Glow className="top-40 -right-20 w-[420px] h-[420px]" color="#FFD166" opacity={0.08} />
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 text-center">
          <Reveal>
            <div className="text-center">
              <Overline
                testId="hero-eyebrow"
                className="!text-sm md:!text-base tracking-[0.28em]"
              >
                A calmer corner of the internet
              </Overline>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5" data-testid="hero-headline" aria-label="ZenScroll — Your Feed Reinvented by AI">
              <img
                src="/brand/zenscroll-logo-glow-transparent.png"
                alt="ZenScroll — Your Feed Reinvented by AI"
                className="w-[260px] sm:w-[320px] md:w-[380px] lg:w-[440px] h-auto mx-auto select-none"
                draggable={false}
              />
            </h1>
          </Reveal>
          <Reveal delay={0.08}>
            {/* Brand statement — DM Serif Display regular, two-line editorial
                headline, centered ("Create more. / Scroll differently."). */}
            <h2
              data-testid="hero-tagline"
              className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-normal text-white leading-[1.08] tracking-[0.01em] max-w-xl mx-auto text-center"
            >
              <span className="block">Create more.</span>
              <span className="block">Scroll differently.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-xl mx-auto text-center">
              ZenScroll pairs AI creativity with intentional pauses — so you create, share, and scroll on your terms.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {token ? (
                <>
                  <button data-testid="hero-cta-create" onClick={() => navigate('/create')} className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-7 py-3.5 hover:bg-[#2A6F72] transition-all shadow-[0_0_24px_rgba(28,71,73,0.5)] hover:scale-[1.02]">
                    Start Creating <ArrowRight className="w-4 h-4" />
                  </button>
                  <button data-testid="hero-cta-explore" onClick={() => navigate('/explore')} className="inline-flex items-center gap-2 bg-transparent text-white border border-white/20 rounded-full px-7 py-3.5 hover:bg-white/5 backdrop-blur-md transition-colors">
                    Explore Posts
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="hero-cta-signup"
                    onClick={() => navigate('/auth?mode=signup')}
                    className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-7 py-3.5 hover:bg-[#2A6F72] transition-all shadow-[0_0_24px_rgba(28,71,73,0.5)] hover:scale-[1.02]"
                  >
                    Start Creating <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="hero-cta-signin"
                    onClick={() => navigate('/explore')}
                    className="inline-flex items-center gap-2 bg-transparent text-white border border-white/20 rounded-full px-7 py-3.5 hover:bg-white/5 backdrop-blur-md transition-colors"
                  >
                    Explore Posts
                  </button>
                </>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            {/* Hidden inside the Capacitor native app — the badge component
                does this check too, but we wrap the eyebrow + spacing here
                so the surrounding column doesn't reserve empty space. */}
            {!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) && (
              <div className="mt-6">
                {/* "Free to download" now leads the block as a proper
                    section header — larger, centered on every breakpoint
                    per Feb-2026 landing polish. */}
                <p
                  data-testid="hero-free-badge"
                  className="text-lg md:text-xl font-semibold text-[#1C4749] text-center mb-4 tracking-tight"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  Free to download
                </p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 text-center">
                  Now on iPhone &amp; Android
                </p>
                <AppStoreBadges align="center" testId="hero-app-store-badges" />
              </div>
            )}
          </Reveal>
        </div>
        <div className="lg:col-span-5 flex justify-center">
          <Reveal delay={0.15}><PhoneMockup /></Reveal>
        </div>
      </div>
    </section>
  );
}

// Soft end-of-page stop — the homepage itself models mindful scrolling.
function CaughtUpCard({ navigate, token }) {
  return (
    <section data-testid="caught-up-card" className="relative py-8 md:py-10">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="mx-auto w-14 h-14 rounded-full border border-[#5EEAD4]/30 bg-[#1C4749]/20 flex items-center justify-center">
          <Check className="w-6 h-6 text-[#5EEAD4]" />
        </div>
        <h2 className="mt-5 text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>
          You&apos;re all caught up
        </h2>
        <p className="mt-3 text-sm md:text-base text-[#94A3B8] leading-relaxed">
          That&apos;s everything for today. A fresh prompt arrives tomorrow — no need to keep scrolling.
        </p>
        <button
          data-testid="caught-up-create-btn"
          onClick={() => navigate(token ? '/create' : '/auth?mode=signup')}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-[#1C4749] text-white hover:bg-[#2A6F72] transition-colors"
        >
          {token ? 'Create something instead' : 'Start creating'} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const diff = Math.max(0, new Date(targetIso).getTime() - now);
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

// DailyChallenge component moved to /app/frontend/src/components/DailyChallenge.jsx —
// the flagship redesign lives there (365-day library, streaks, badges,
// rewards, weekly themes, confetti, animated counters).


// ---------- Explore-side sections (merged in from former /explore page) ----------

/**
 * Quiz/poll preview tile for the Popular & Community grids. Renders the lyric
 * (or poll question) on a themed gradient — no SafeImage attempt, so no
 * broken-image placeholder when the content has no `url`.
 */
function PollPreview({ p, testId, compact = false, fill = false }) {
  const theme = p.theme || { bg: '#101F1D', fg: '#5EEAD4' };
  const isQuiz = p.metadata?.is_quiz;
  return (
    <div
      data-testid={testId}
      className={`relative ${fill ? 'h-full' : compact ? 'aspect-square' : 'aspect-[4/5]'} w-full p-5 sm:p-6 flex flex-col justify-between`}
      style={{ background: `linear-gradient(135deg, ${theme.bg}, ${theme.fg}22 95%)` }}
    >
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold self-start" style={{ color: theme.fg }}>
        <BarChart3 className="w-3.5 h-3.5" />
        {quizLabel(p.metadata)}
      </div>
      <p
        className="text-base sm:text-lg leading-snug text-white italic line-clamp-5 mt-3"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {p.text || p.prompt}
      </p>
      <div className="space-y-1.5 mt-3">
        {(p.options || []).slice(0, 3).map((opt) => (
          <div key={opt.id} className="px-2.5 py-1.5 rounded-lg bg-white/8 border border-white/10 text-[11px] text-white/85 truncate">
            {opt.label}
          </div>
        ))}
      </div>
      <span className="text-[10px] uppercase tracking-wider mt-3 self-start" style={{ color: theme.fg }}>
        {isQuiz ? 'Tap to play' : 'Tap to vote'}
      </span>
    </div>
  );
}

function VideoTile({ p, className = '', testId }) {
  return (
    <InViewVideo
      data-testid={testId}
      src={p.url}
      poster={p.poster_url || undefined}
      muted
      loop
      playsInline
      preload="metadata"
      className={className}
    />
  );
}

function PromptOfTheDay({ potd, navigate, token }) {
  // Dynamic daily prompt — 300-prompt / 30-category library, keyed to the
  // user's LOCAL calendar day (flips at local midnight). The dateKey
  // re-check keeps long-lived native sessions fresh across midnight.
  const [dateKey, setDateKey] = useState(() => new Date().toDateString());
  useEffect(() => {
    const check = () => {
      const k = new Date().toDateString();
      setDateKey((prev) => (prev === k ? prev : k));
    };
    const id = setInterval(check, 60000);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', check); };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dateKey re-derives today's prompt
  const daily = useMemo(() => getDailyPrompt(), [dateKey]);
  const promptText = daily.prompt;
  const goToCreate = (withPrompt) => {
    if (!token) { navigate('/auth?mode=signup'); return; }
    if (withPrompt) navigate(`/create?prompt=${encodeURIComponent(promptText)}&daily=1`);
    else navigate('/create');
  };
  return (
    <section id="prompt" data-testid="prompt-of-the-day" className="relative py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <div className="relative overflow-hidden min-h-[480px] flex items-center justify-center p-10 md:p-16 border border-white/10">
          <img src="https://images.unsplash.com/photo-1444044205806-38f3ed106c10?w=2000&q=75" alt="" className="pointer-events-none absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0A1514] via-[#0A1514]/85 to-[#101F1D]/70" />
          <Glow className="-top-32 left-1/4 w-[420px] h-[420px]" color="#1C4749" opacity={0.16} />
          <div className="relative max-w-3xl text-center">
            <Overline testId="potd-overline">Prompt of the Day</Overline>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] uppercase tracking-widest font-semibold">
              <span data-testid="potd-category" className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">{daily.emoji} {daily.category}</span>
              <span
                data-testid="potd-day-badge"
                className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80"
              >
                {daily.dayNumber} mindful {daily.dayNumber === 1 ? 'day' : 'days'} this season
              </span>
              <span className="px-3 py-1 rounded-full bg-[#1C4749]/15 border border-[#1C4749]/40 text-[#1C4749]">{(potd?.posts_today ?? 1247).toLocaleString()} posts today</span>
            </div>
            <p data-testid="potd-prompt" className="mt-7 text-3xl md:text-5xl text-white leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
              &ldquo;{promptText}&rdquo;
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="potd-use-btn" onClick={() => goToCreate(true)} className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-7 py-3.5 hover:bg-[#2A6F72] transition-all shadow-[0_0_24px_rgba(28,71,73,0.5)]">
                Use This Prompt <Wand2 className="w-4 h-4" />
              </button>
              <button data-testid="potd-own-btn" onClick={() => goToCreate(false)} className="inline-flex items-center gap-2 bg-transparent text-white border border-white/20 rounded-full px-7 py-3.5 hover:bg-white/5 backdrop-blur-md transition-colors">
                Generate My Own
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Share helper for home-page content tiles — mirrors the ZenFlow share action.
async function shareToZenflow(p) {
  const url = `${window.location.origin}/zenflow`;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'ZenScroll', text: p?.prompt || 'A mindful creation on ZenScroll', url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    }
  } catch { /* user dismissed the share sheet */ }
}

// Text-canvas content types render their own typography — the tile overlay
// skips the title for these so the copy never doubles up.
const TEXT_TILE_TYPES = new Set(['quote', 'fact', 'did_you_know', 'poll']);

const VideoPill = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold bg-black/60 text-white border border-white/10">
    Video
  </span>
);

function PopularPosts({ posts, navigate, token }) {
  const [removedIds, setRemovedIds] = useState(new Set());
  // Most-loved creations is a hand-picked showcase (per Feb-2026 product
  // direction). The curated constant is authoritative so specific videos
  // (crashing waves, snowy ski lodge) always render; backend posts are only
  // used as a last-resort fallback if the constant is emptied at build time.
  const list = (POPULAR_POSTS_FALLBACK.length ? POPULAR_POSTS_FALLBACK : (posts || []))
    .filter((p) => !removedIds.has(p.id))
    .slice(0, 6);
  return (
    <section id="popular-section" data-testid="popular-section" className="relative py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <Reveal>
          <Overline>Popular Today</Overline>
          <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-white tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>Most-loved creations</h2>
            <button onClick={() => navigate(token ? '/zenflow' : '/auth')} className="text-sm text-[#1C4749] hover:text-white transition-colors">See full feed →</button>
          </div>
        </Reveal>
        {/* Tiles span the full max-w-7xl container so they align with the
            Prompt of the Day field width (user request, 2026-06). ZenFlow
            presentation language: hard corners, overlay title/creator,
            right-edge interaction rail (2026-06 consistency update).
            On phones the tiles bleed edge-to-edge (-mx-4 cancels the page
            gutter) for the immersive mobile parity pass. */}
        <div className="mt-6 grid grid-cols-1 gap-6 -mx-4 sm:mx-0">
          {list.map((p, i) => {
            const isText = TEXT_TILE_TYPES.has(p.type);
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                <ZenTile
                  testId={`popular-post-${p.id}`}
                  aspect="aspect-[4/5] sm:aspect-[16/10]"
                  title={isText ? undefined : (p.prompt || 'A quiet creation')}
                  caption={isText ? undefined : p.caption}
                  username={p.user?.username || 'zenscroll'}
                  onOpen={() => navigate(token ? '/zenflow' : '/auth')}
                  topRight={p.type === 'video' ? <VideoPill /> : null}
                  actions={[
                    { key: 'appreciate', label: 'Appreciate', Icon: Heart, onClick: () => navigate(token ? '/zenflow' : '/auth?mode=signup') },
                    { key: 'create-similar', label: 'Create Similar', Icon: Wand2, accent: true, onClick: () => navigate(token ? `/create?prompt=${encodeURIComponent(p.prompt || '')}` : '/auth?mode=signup') },
                    { key: 'share', label: 'Share', Icon: Share2, onClick: () => shareToZenflow(p) },
                  ]}
                >
                  {(p.type === 'quote' || p.type === 'fact' || p.type === 'did_you_know') ? (
                    <div
                      className="w-full h-full p-6 flex flex-col justify-center bg-gradient-to-br from-[#1C4749]/35 via-[#101F1D] to-[#0A1514]"
                      data-testid={`popular-text-${p.id}`}
                    >
                      <Sparkles className="w-6 h-6 text-[#5EEAD4] mb-3" />
                      <p className="text-base sm:text-lg leading-snug text-white italic line-clamp-6" style={{ fontFamily: 'var(--font-display)' }}>
                        {'\u201C'}{p.prompt}{'\u201D'}
                      </p>
                    </div>
                  ) : p.type === 'poll' ? (
                    <PollPreview p={p} testId={`popular-poll-${p.id}`} fill />
                  ) : p.type === 'video' ? (
                    <VideoTile p={p} className="w-full h-full object-cover" testId={`popular-video-${p.id}`} />
                  ) : (
                    <SafeImage
                      src={p.url || p.poster_url}
                      alt={p.prompt}
                      prompt={p.prompt}
                      className="w-full h-full"
                      imgClassName="w-full h-full object-cover"
                      testId={`popular-img-${p.id}`}
                    />
                  )}
                  <AdminDeleteButton
                    contentId={p.id}
                    onDeleted={(id) => setRemovedIds((s) => new Set([...s, id]))}
                    testId={`popular-${p.id}`}
                  />
                </ZenTile>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TrendingCategoriesRow({ data, direction, testId, navigate }) {
  return (
    <div className="overflow-hidden relative">
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0A1514] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0A1514] to-transparent z-10 pointer-events-none" />
      <motion.div
        data-testid={testId}
        animate={{ x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%'] }}
        transition={{ duration: 50, ease: 'linear', repeat: Infinity }}
        className="flex gap-3 w-max"
      >
        {data.map((c, i) => (
          <button
            key={`${c.label}-${i}`}
            onClick={() => navigate('/zenflow')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white/80 hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap"
          >
            <c.Icon className="w-4 h-4 text-[#1C4749]" /> {c.label}
          </button>
        ))}
      </motion.div>
    </div>
  );
}

function TrendingCategories({ navigate }) {
  // Split the 11 categories into two disjoint halves so no category
  // appears in both rows (Aug-2026 dedup fix — Sports/Wellness/Food/
  // Animals/Positive News/Nature were surfacing twice, reading as a
  // rendering bug). Each row still duplicates its own half internally
  // to keep the infinite-marquee loop seamless.
  const mid = Math.ceil(TRENDING_CATEGORIES.length / 2);
  const halfA = TRENDING_CATEGORIES.slice(0, mid);
  const halfB = TRENDING_CATEGORIES.slice(mid);
  const rowA = [...halfA, ...halfA];
  const rowB = [...halfB.slice().reverse(), ...halfB.slice().reverse()];
  return (
    <section id="trending" data-testid="trending-section" className="relative py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <Reveal>
          <Overline>Trending Categories</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl text-white tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>Find your corner of calm</h2>
        </Reveal>
      </div>
      <div className="mt-6 space-y-4 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <TrendingCategoriesRow data={rowA} direction="left" testId="trending-row-1" navigate={navigate} />
        <TrendingCategoriesRow data={rowB} direction="right" testId="trending-row-2" navigate={navigate} />
      </div>
    </section>
  );
}




function FreshFromCommunity({ feed, navigate, token }) {
  const [filter, setFilter] = useState('Newest');
  const [removedIds, setRemovedIds] = useState(new Set());
  const list = ((feed && feed.length ? feed : COMMUNITY_FEED_FALLBACK)).filter((p) => !removedIds.has(p.id));
  return (
    <section data-testid="community-section" className="relative py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <Reveal>
          <Overline>From the Community</Overline>
          <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-white tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>Fresh from the community</h2>
          </div>
        </Reveal>
        <div className="relative mt-7">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#0A1514] via-[#0A1514]/70 to-transparent z-[1]"
          />
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {COMMUNITY_FEED_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`community-filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${filter === f ? 'bg-white text-[#0A1514] border-white' : 'bg-white/5 text-white/75 border-white/10 hover:bg-white/10 hover:text-white'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {list.map((p, i) => {
            const isText = TEXT_TILE_TYPES.has(p.type);
            return (
              <Reveal key={p.id} delay={(i % 6) * 0.05} className="mb-6 break-inside-avoid">
                <ZenTile
                  testId={`community-post-${p.id}`}
                  aspect="aspect-square"
                  title={isText ? undefined : p.prompt}
                  username={p.user?.username || 'zenscroll'}
                  onOpen={() => navigate(token ? '/zenflow' : '/auth?mode=signup&intent=feed')}
                  topRight={p.type === 'video' ? <VideoPill /> : null}
                  actions={[
                    { key: 'appreciate', label: 'Appreciate', Icon: Heart, onClick: () => navigate(token ? '/zenflow' : '/auth?mode=signup') },
                    { key: 'create-similar', label: 'Create Similar', Icon: Wand2, accent: true, onClick: () => navigate(token ? `/create?prompt=${encodeURIComponent(p.prompt || '')}` : '/auth?mode=signup') },
                    { key: 'share', label: 'Share', Icon: Share2, onClick: () => shareToZenflow(p) },
                  ]}
                >
                  {(p.type === 'quote' || p.type === 'fact' || p.type === 'did_you_know') ? (
                    <div
                      className="w-full h-full p-5 flex flex-col justify-center bg-gradient-to-br from-[#1C4749]/35 via-[#101F1D] to-[#0A1514]"
                      data-testid={`community-text-${p.id}`}
                    >
                      <Sparkles className="w-5 h-5 text-[#5EEAD4] mb-2.5" />
                      <p className="text-sm sm:text-base leading-snug text-white italic line-clamp-6" style={{ fontFamily: 'var(--font-display)' }}>
                        {'\u201C'}{p.prompt}{'\u201D'}
                      </p>
                    </div>
                  ) : p.type === 'poll' ? (
                    <PollPreview p={p} testId={`community-poll-${p.id}`} fill />
                  ) : p.type === 'video' && !p.poster_url ? (
                    <VideoTile p={p} className="w-full h-full object-cover" testId={`community-video-${p.id}`} />
                  ) : (
                    <SafeImage
                      src={p.type === 'video' ? (p.poster_url || p.url) : (p.url || p.poster_url)}
                      alt={p.prompt}
                      prompt={p.prompt}
                      className="w-full h-full"
                      imgClassName="w-full h-full object-cover"
                      testId={`community-img-${p.id}`}
                    />
                  )}
                  <AdminDeleteButton
                    contentId={p.id}
                    onDeleted={(id) => setRemovedIds((s) => new Set([...s, id]))}
                    testId={`community-${p.id}`}
                  />
                </ZenTile>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6 text-center">
          <button
            data-testid="community-see-more-btn"
            onClick={() => navigate(token ? '/trending' : '/auth?mode=signup&intent=feed')}
            className="inline-flex items-center gap-2 bg-transparent text-white border border-white/15 rounded-full px-7 py-3 hover:bg-white/5 hover:border-[#1C4749]/40 transition-colors"
          >
            See more <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}


// "About" mini-section shown on the home page for logged-out visitors.
// Sourced from /about (condensed): mission + three value pillars + CTA.
function AboutSummary({ navigate }) {
  const PILLARS = [
    {
      icon: Heart,
      title: 'Human-centered',
      body: 'Technology should serve people. Every AI feature is designed to enhance human connection, creativity, and well-being.',
    },
    {
      icon: Shield,
      title: 'Safety by design',
      body: 'Our AI is trained to recognize and reduce harmful content, misinformation, and harassment — so the community can thrive.',
    },
    {
      icon: Eye,
      title: 'Trust & transparency',
      body: 'Clear communication about how our AI works, what data it uses, and how your experience is shaped.',
    },
  ];

  return (
    <section
      data-testid="home-about-section"
      className="relative py-8 md:py-10 border-t border-white/5"
    >
      <Glow className="-top-20 right-1/4 w-[440px] h-[440px]" color="#1C4749" opacity={0.1} />
      <div className="max-w-6xl mx-auto px-6 md:px-8 lg:px-12">
        <Reveal>
          <Overline testId="home-about-eyebrow">About ZenScroll</Overline>
          <h2
            className="mt-3 text-3xl md:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            A new way to be <span className="italic text-[#1C4749]">social</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-3xl">
            ZenScroll is the world&apos;s first AI-powered social media platform designed to
            revolutionize the way we connect, create, and communicate — smarter, safer, and
            more meaningful.
          </p>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div className="h-full bg-[#101F1D]/60 backdrop-blur-xl border border-white/10 p-6 md:p-7 hover:border-white/20 transition-colors">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#1C4749]/15 text-[#1C4749]">
                  <p.icon className="w-5 h-5" />
                </span>
                <h3
                  className="mt-5 text-xl text-white"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.25}>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              data-testid="home-about-cta-signup"
              onClick={() => navigate('/auth?mode=signup')}
              className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-7 py-3.5 hover:bg-[#2A6F72] transition-all shadow-[0_0_24px_rgba(28,71,73,0.5)] hover:scale-[1.02]"
            >
              Join ZenScroll <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="home-about-cta-read-more"
              onClick={() => navigate('/about')}
              className="inline-flex items-center gap-2 bg-transparent text-white border border-white/20 rounded-full px-7 py-3.5 hover:bg-white/5 transition-colors"
            >
              Read our story
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WelcomeMarketing({ token }) {
  const navigate = useNavigate();
  const [heroPosts, setHeroPosts] = useState([]);
  // Explore-side state (merged in from former /explore page)
  const [showcase, setShowcase] = useState({ whats_new: [], popular: [] });
  const [potd, setPotd] = useState(null);

  // Single combined showcase + prompt-of-the-day fetch — feeds Hero PhoneMockup,
  // PopularPosts grid, FreshFromCommunity grid, and PromptOfTheDay.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sc, p] = await Promise.all([
          axios.get(`${API}/public/showcase?limit=12`),
          axios.get(`${API}/public/prompt-of-the-day`),
        ]);
        if (cancelled) return;
        const data = sc.data || { whats_new: [], popular: [] };
        setShowcase(data);
        const heroArr = (data.whats_new || []).slice(0, 8).map((p) => ({
          id: p.id, url: p.url, prompt: p.prompt, user: p.user, likes_count: p.likes_count,
        }));
        setHeroPosts(heroArr);
        setPotd(p.data || null);
      } catch (e) {
        // mock fallbacks render automatically
        if (process.env.NODE_ENV !== 'production') console.warn('[welcome] hero/potd fetch failed', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => { if (badge) badge.style.display = 'flex'; };
  }, []);

  const popular = (showcase.popular || []).map((p) => ({
    id: p.id, url: p.url, prompt: p.prompt, user: p.user,
    type: p.type, poster_url: p.poster_url,
    text: p.text, options: p.options, theme: p.theme, metadata: p.metadata,
    likes_count: p.likes_count ?? 0,
    comments_count: p.comments_count, shares_count: p.shares_count,
  }));

  const communityFeed = (showcase.whats_new || []).map((p) => ({
    id: p.id, url: p.url, prompt: p.prompt, user: p.user,
    type: p.type, poster_url: p.poster_url,
    text: p.text, options: p.options, theme: p.theme, metadata: p.metadata,
    likes_count: p.likes_count ?? 0,
  }));

  return (
    <div className="min-h-screen bg-[#0A1514] text-white relative" style={{ fontFamily: 'var(--font-sans)' }} data-testid="welcome-page">
      <GrainOverlay />
      {/* Public landing: header scrolls inline with content (iOS TestFlight
          requirement).  Authenticated visitors still get the pinned header so
          the search/profile chip stays one tap away while browsing the feed. */}
      <Nav navigate={navigate} token={token} staticHeader={!token} />
      <main className="relative z-10">
        <Hero navigate={navigate} posts={heroPosts} token={token} />
        {/* Reorder Aug 2026 — lead with proof (real user creations) before
            game mechanics. Section order below intentionally puts the
            most-loved gallery immediately after the hero. */}
        <PopularPosts posts={popular} navigate={navigate} token={token} />
        <PromptOfTheDay potd={potd} navigate={navigate} token={token} />
        <section data-testid="prompt-engagement-section" className="relative pb-4">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
            <WordOfTheDay />
          </div>
        </section>
        <DailyChallenge navigate={navigate} token={token} />
        <TrendingCategories navigate={navigate} />
        {!token && <AboutSummary navigate={navigate} />}
        <CaughtUpCard navigate={navigate} token={token} />
      </main>
      <Footer />
      <ScrollToTopPill />
    </div>
  );
}

// Public export.  Picks between the native pre-login screen and the full web
// marketing landing.  Web users always get `WelcomeMarketing`.  Native users
// get `NativePrelogin` ONLY when unauthenticated — once they sign in, they
// continue into the normal authenticated experience via routing in App.js.
export default function Welcome({ token }) {
  if (IS_NATIVE_APP && !token) {
    return <NativePrelogin />;
  }
  return <WelcomeMarketing token={token} />;
}

